import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { OrderRecords } from '@/api/types';
import { dateTime, num, qty } from '@/lib/format';
import { ErrorState, Skeleton } from '@/components/States';
import { transactionKind } from '@/features/inventory/status';
import type { InventoryTransaction } from '@/features/inventory/types';
import { useResource } from './api';
import s from './OrderRecordsPanel.module.css';

type Category = 'status' | 'change' | 'inventory' | 'exception';
export type OrderRecordsSummary = { count: number; submittedAt?: string; approvedAt?: string; assemblyAt?: string; firstShipmentAt?: string };
type DiffRow = { label: string; before: string; after: string };
type Entry = { id: string; category: Category; title: string; note: string; actor: string; at: string; details?: DiffRow[]; blocked?: boolean };

const SENSITIVE_DIFF_KEYS = new Set(['totalAmount', 'productAmount', 'shippingFee', 'discountAmount', 'actualShippingFee', 'depositAmount', 'balanceAmount', 'unitPrice', 'bundleUnitPrice', 'amount']);
function describeDiff(diff: unknown, canAmount: boolean) {
  if (!Array.isArray(diff)) return [];
  return diff.flatMap((value) => {
    if (!value || typeof value !== 'object') return [];
    const row = value as Record<string, unknown>;
    const key = String(row.key || '');
    if (!canAmount && SENSITIVE_DIFF_KEYS.has(key)) return [];
    const before = String(row.before ?? '—');
    const after = String(row.after ?? '—');
    return [{ label: String(row.label || key || '字段'), before: !canAmount && key.startsWith('line:') ? before.split('，')[0] : before, after: !canAmount && key.startsWith('line:') ? after.split('，')[0] : after }];
  });
}

const ACTIONS: Record<string, string> = {
  'order.create': '新建订单', 'order.submit': '提交预占', 'order.approve': '审核分配',
  'order.update': '更新订单', 'order.status.update': '更新流程状态', 'order.invoice.update': '更新开票信息',
  'order.cancel': '取消订单', 'order.close': '部分履约结案', 'shipment.create': '新增发货',
  'order.exception.create': '登记异常', 'order.exception.update': '处理异常', 'order.exception.delete': '删除异常',
  'order.exception.reship': '异常补发', 'order.exception.return_inbound': '退货入库',
};
const TABS: { key: Category | 'all'; label: string }[] = [
  { key: 'all', label: '全部' }, { key: 'status', label: '状态变更' }, { key: 'change', label: '变更单' },
  { key: 'inventory', label: '库存' }, { key: 'exception', label: '异常' },
];

function entries(data: OrderRecords, transactions: InventoryTransaction[], canAmount: boolean): Entry[] {
  const operations = [...data.operations, ...(data.createdRecord ? [data.createdRecord] : [])].map((item): Entry => ({
    id: item.id,
    category: item.action.includes('exception') ? 'exception' : item.action.includes('stock') || item.action.includes('inventory') ? 'inventory' : 'status',
    title: ACTIONS[item.action] || '订单操作',
    note: canAmount ? item.summary || '' : '',
    actor: item.user?.name || '系统',
    at: item.createdAt,
  }));
  const changes = data.changeRequests.map((request): Entry => ({
    id: `change-${request.id}`,
    category: 'change',
    title: request.status === 'PENDING' ? '订单变更待审核' : request.status === 'REJECTED' ? '订单变更已驳回' : request.status === 'APPLYING' ? '变更正在生效' : request.status === 'SUPERSEDED' ? '旧变更已被替代' : request.followerAcknowledgedAt ? '跟单员已确认知晓变更' : '变更已生效，等待跟单确认',
    note: request.reason || '',
    details: describeDiff(request.diff, canAmount),
    blocked: request.status === 'APPROVED' && !request.followerAcknowledgedAt,
    actor: request.reviewer?.name || request.requester?.name || '系统',
    at: request.reviewedAt || request.createdAt,
  }));
  const inventory = transactions.map((item): Entry => {
    const amount = num(item.quantity);
    const outgoing = amount < 0 || item.direction === 'OUT' || item.direction === 'RELEASE';
    return {
      id: `inventory-${item.id}`,
      category: 'inventory',
      title: transactionKind(item).label,
      note: `${item.product.name} · ${outgoing ? '−' : '+'}${qty(Math.abs(amount))} 个`,
      actor: item.createdBy?.name || '系统',
      at: item.createdAt,
    };
  });
  return [...operations, ...changes, ...inventory].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

export function OrderRecordsPanel({ id, canAmount, canInventory, exportInHeader = false, onExportReady, onSummaryReady, hideStockLink = false }: { id: string; canAmount: boolean; canInventory: boolean; exportInHeader?: boolean; onExportReady?: (action: (() => void) | null) => void; onSummaryReady?: (summary: OrderRecordsSummary | null) => void; hideStockLink?: boolean }) {
  const { data, loading, error, reload } = useResource<OrderRecords>(`/api/orders/${id}/records`);
  const inventory = useResource<InventoryTransaction[]>(canInventory ? `/api/orders/${id}/inventory-transactions` : null);
  const [filter, setFilter] = useState<Category | 'all'>('all');
  const all = useMemo(() => data ? entries(data, canInventory ? inventory.data || [] : [], canAmount) : [], [data, inventory.data, canAmount, canInventory]);
  const visible = filter === 'all' ? all : all.filter((entry) => entry.category === filter);
  const exportRecords = useCallback(() => {
    const rows = [['时间', '类型', '事项', '操作人', '说明'], ...visible.map((entry) => [dateTime(entry.at), TABS.find((tab) => tab.key === entry.category)?.label || '状态', entry.title, entry.actor, [entry.note, ...(entry.details || []).map((detail) => `${detail.label}：${detail.before} → ${detail.after}`)].filter(Boolean).join('；')])];
    const csv = '\uFEFF' + rows.map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = '订单记录.csv';
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [visible]);
  useEffect(() => {
    onExportReady?.(exportRecords);
    return () => onExportReady?.(null);
  }, [exportRecords, onExportReady]);
  useEffect(() => {
    if (loading || error || !data) { onSummaryReady?.(null); return; }
    const earliest = (predicate: (action: string, summary: string) => boolean) => data.operations
      .filter((item) => predicate(item.action, item.summary || ''))
      .map((item) => item.createdAt)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
    onSummaryReady?.({
      count: all.length,
      submittedAt: earliest((action) => action === 'order.submit'),
      approvedAt: earliest((action) => action === 'order.approve'),
      assemblyAt: earliest((action, summary) => action === 'order.status.update' && summary.includes('开始组装')),
      firstShipmentAt: earliest((action) => action === 'shipment.create'),
    });
  }, [all.length, data, error, loading, onSummaryReady]);
  if (loading || canInventory && inventory.loading) return <div className={s.loading}><Skeleton lines={5} /></div>;
  if (error) return <ErrorState title="订单记录加载失败" message={error.message} detail={`GET /api/orders/${id}/records`} onRetry={reload} />;
  return <div className={s.panel}>
    {canInventory && inventory.error && <p role="alert">库存事件加载失败：{inventory.error.message} <button type="button" onClick={inventory.reload}>重试</button>。下方记录暂不完整。</p>}
    <div className={s.toolbar} role="group" aria-label="记录类型">
      {TABS.map((tab) => <button type="button" key={tab.key} className={filter === tab.key ? s.on : ''} aria-pressed={filter === tab.key} onClick={() => setFilter(tab.key)}>{tab.label} <span>{tab.key === 'all' ? all.length : all.filter((entry) => entry.category === tab.key).length}</span></button>)}
      {canInventory && !hideStockLink && <Link to={`/orders/${id}/stock`} className={s.stockLink}>库存流水</Link>}
      {!exportInHeader && <button type="button" className={s.export} disabled={!visible.length} onClick={exportRecords}>导出记录</button>}
    </div>
    {visible.length ? <ol className={s.timeline}>{visible.map((entry) => <li key={entry.id} className={`${s[entry.category]} ${entry.blocked ? s.blocked : ''}`}>
      <i aria-hidden="true" /><div><div className={s.lineHead}><strong>{entry.title}</strong>{entry.blocked && <span className={s.blockedTag}>阻塞中</span>}<time dateTime={entry.at}>{dateTime(entry.at)}</time></div>{entry.note && <p>{entry.note}</p>}{entry.details?.length ? <ul className={s.diffs}>{entry.details.map((detail, index) => <li key={index}><span>{detail.label}</span><del>{detail.before}</del><span aria-hidden="true">→</span><b>{detail.after}</b></li>)}</ul> : null}<small>{entry.actor}</small></div>
    </li>)}</ol> : <p className={s.empty}>这个类型暂无记录。</p>}
  </div>;
}
